//@ts-ignore-next-line
import { createVuetify, type ThemeDefinition } from 'vuetify';
import { md3 } from 'vuetify/blueprints';
import { VAlert, VBtn, VSelect, VTextField } from 'vuetify/components';

import vuetifyLocalization from '@/helpers/constants/vuetifyLocalization.json';

import 'vuetify/styles';
const myCustomLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    tertiary: '#4a607b',
    error: '#FB2C36',
    'error-light': '#FFEFEF',
    danger: '#ba1a1a',
    gray: '#7d7d7d',
    light: '#FBFBFB',
    hint: '#ececec',
    'table-bg': '#F9FAFC',
    'chip-bg': '#FEF7FF',
    'chip-text': '#49454F',
    'gray-100': '#00000033',
    'black-400': '#8F8F8F',
    'black-700': '#3B3B3B',
    'black-800': '#1C1C1C',
    'black-900': '#010101',
    'black-600': '#575757',
    'black-500': '#737373',

    'blue-700': '#004299',
    'blue-300': '#05DBF2',
    'blue-50': '#E5F1FF',
    'blue-75': '#BDDCF6',
    'blue-1': '#0583F2',
    'blue-2': '#004396',
    'blue-3': '#05DBF2',
    'blue-4': '#2A7DE1',
    main: '#1F2F49',
    white: '#ffffff',
    black: '#000000',
    background: '#fafdfc',
    outline: '#6f7978',
    'dark-gray': '#323639',
    'light-pink': '#F5EDFF',
    primary: '#00416b',
    header: '#00416b',
    success: '#086C1F',
    'gray-1': '#707070',
    secondary: '#00c08b',
    'gray-2': '#EAECEF',
    'gray-3': '#465461',
    info: '#54c0e8',
    'dark-text': '#404040',
    blue: '#75ADDF',
    text: '#666666',
    'black-200': '#C7C7C7',
    'blue-800': '#002C66',
    'black-300': '#ABABAB',
    'black-20': '#F5F5F5',
    'blue-text': '#465465',
    linkedin: '#0A66C2',
    facebook: '#0866FF',
    yellow: '#F5B335',
    themeIcon: '#E5F1FF',
    themeBtn: '#002C66',
    tabbyWidget: '#FFFFFF',
    hoverReviewCard: '#F4F4F4',
    reviewCard: '#FCFCFC',
    warning: '#FF5900',
    'light-orange': '#FFF0C3',
    selectButton: '#F3F3F5',
    hoverButton: '#F2F2F2',
    selectButtonBorder: '#7f86a7ff',
    'red-100': '#FFBDC0',
    'warning-text': '#894B00',
    'warning-bg': '#FEFCE8',
    'success-bg': '#BBECC2',
    'error-bg': '#FFEBE7',
  },
  variables: {
    'high-emphasis-opacity': 0.87, // default 0.87
    'medium-emphasis-opacity': 0.6, // default 0.6
    'disabled-opacity': 0.38, // default 0.38
    'idle-opacity': 0.04, // default 0.04
    'hover-opacity': 0.04, // default 0.04
    'focus-opacity': 0.12, // default 0.12
    'selected-opacity': 0.08, // default 0.08
    'activated-opacity': 0.12, // default 0.12
    'pressed-opacity': 0.12, // default 0.12
    'dragged-opacity': 0.08, // default 0.08
  },
};

//TODO someone fix this pls, on refresh the header and background are messed up
const myCustomDarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    black: '#252f38',
    primary: '#4e98bf', // The Old color was #1867C0 I used this one to make it more visible on the dark theme [Based On Material Design Color Docs]
    header: '#00416b',
    success: '#00c08b',
    'gray-1': '#707070',
    danger: '#d02c2f',
    secondary: '#00c08b',
    background: '#121212',
    light: '#393939',

    info: '#54c0e8',
    'dark-text': '#404040',
    blue: '#75ADDF',
    text: '#BDBDBD',
  },
  variables: {
    'high-emphasis-opacity': 0.87, // default 0.87
    'medium-emphasis-opacity': 0.6, // default 0.6
    'disabled-opacity': 0.38, // default 0.38
    'idle-opacity': 0.04, // default 0.04
    'hover-opacity': 0.04, // default 0.04
    'focus-opacity': 0.12, // default 0.12
    'selected-opacity': 0.08, // default 0.08
    'activated-opacity': 0.12, // default 0.12
    'pressed-opacity': 0.12, // default 0.12
    'dragged-opacity': 0.08, // default 0.08
  },
};
const savedTheme = localStorage.getItem('theme');

const vuetify = createVuetify({
  // https://vuetifyjs.com/en/features/treeshaking/
  blueprint: md3,
  theme: {
    defaultTheme:
      import.meta.env.VITE_HIDE_DARK_THEME === 'true'
        ? 'myCustomLightTheme'
        : savedTheme || 'myCustomLightTheme',
    // defaultTheme: 'myCustomLightTheme',
    themes: {
      myCustomLightTheme,
      myCustomDarkTheme,
    },
    variations: {
      colors: ['primary', 'secondary'],
      lighten: 6,
      darken: 2,
    },
  },
  locale: {
    locale: 'en',
    fallback: 'en',
    messages: vuetifyLocalization,
  },
  aliases: {
    // use vuetify components as baseline for your custom component
    VCustomTextField: VTextField,
    VCustomBtn: VBtn,
    VCancelBtn: VBtn,
    VError: VAlert,
    VOutlineField: VTextField,
    VOutlineSelect: VSelect,
    VBtnText: VBtn,
  },

  defaults: {
    VBtn: {
      class: 'text-capitalize',
      rounded: 'xs',
      color: '',
    },
    // set default prop values for vuetify components
    VCustomTextField: {
      class: 'flex-grow-0 glex-shrink-0',
      variant: 'outlined',
    },
    VCustomBtn: {
      color: 'primary',
      class: 'text-capitalize px-4 py-3 h-auto w-auto',
      size: 'default',
      'min-width': '200px',
      rounded: 'xs',
    },
    VCancelBtn: {
      variant: 'text',
      class: 'text-capitalize px-4 py-3 h-auto',
      size: 'default',
      rounded: 'xs',
      color: '',
    },
    VAutocomplete: {
      variant: 'outlined',
      density: 'default',
    },
    VTab: {
      class: 'text-capitalize',
    },
    VBtnText: {
      variant: 'text',
      class: 'text-capitalize text-subtitle-2',
      color: 'primary',
      rounded: 0,
      height: '35px',
    },
    VOutlineField: {
      variant: 'outlined',
      density: 'compact',
      class: '  rounded',
    },
    VOutlineSelect: {
      variant: 'outlined',
      density: 'compact',
      class: '  rounded',
    },
  },
});

export default vuetify;
